import { createSlice } from '@reduxjs/toolkit';

import { formatDate, subDate } from 'common/utils/dates';

import {
    FETCH_AGENTS,
    FETCH_AGENTS_FAILURE,
    FETCH_AGENTS_SUCCESS,
    FETCH_COMPANY_DETAIL,
    FETCH_COMPANY_DETAIL_FAILURE,
    FETCH_COMPANY_DETAIL_SUCCESS,
    FETCH_PROJECTS_COUNTERS,
    FETCH_PROJECTS_COUNTERS_FAILURE,
    FETCH_PROJECTS_COUNTERS_SUCCESS,
    FETCH_PROPOSALS_TOTALS,
    FETCH_PROPOSALS_TOTALS_FAILURE,
    FETCH_PROPOSALS_TOTALS_SUCCESS,
    INITIALIZE_FILTER_FORM,
    INITIALIZE_FORM,
    RESET,
    SAVE,
    SAVE_FAILURE,
    SAVE_SUCCESS,
} from './actionTypes';
import { NAME } from './constants';

const initialDate = formatDate(subDate(new Date(), { years: 1 }), 'dd/MM/yyyy');
const finalDate = formatDate(new Date(), 'dd/MM/yyyy');

const INITIAL_STATE = {
    agents: {
        data: [],
        error: null,
        isFetching: false,
    },
    initialFilterValues: {
        agent: '',
        final_date: finalDate,
        initial_date: initialDate,
    },
    initialValues: {
        id: null,
        active_users: null,
        business_name: null,
        rfc: null,
        owner: null,
    },
    companyDetail: {
        isFetching: false,
        data: {},
        errors: null,
    },
    projectsCounters: {
        data: {
            categories: [],
            series: [],
            projects_totals: 0,
        },
        error: null,
        isEmpty: false,
        isFetching: false,
    },
    proposalsTotals: {
        data: {
            categories: [],
            series: [],
            projects_number: 0,
            projects_totals: 0,
            projects_kws: 0,
        },
        error: null,
        isEmpty: false,
        isFetching: false,
    },
    save: {
        isSaving: false,
        data: {},
        errors: null,
    },
};

const companyGeneralSlice = createSlice({
    name: NAME,
    initialState: INITIAL_STATE,
    reducers: {
        [FETCH_AGENTS]: (state) => {
            state.agents = { ...state.agents, errors: [], isFetching: true };
        },
        [FETCH_AGENTS_FAILURE]: (state, action) => {
            state.agents = {
                ...state.agents,
                errors: action.payload,
                isFetching: false,
            };
        },
        [FETCH_AGENTS_SUCCESS]: (state, action) => {
            state.agents = {
                ...state.agents,
                data: action.payload,
                isFetching: false,
            };
        },
        [FETCH_COMPANY_DETAIL]: (state) => {
            state.companyDetail = {
                ...state.companyDetail,
                errors: [],
                isFetching: true,
            };
        },
        [FETCH_COMPANY_DETAIL_FAILURE]: (state, action) => {
            state.companyDetail = {
                ...state.companyDetail,
                errors: action.payload,
                isFetching: false,
            };
        },
        [FETCH_COMPANY_DETAIL_SUCCESS]: (state, action) => {
            state.companyDetail = {
                ...state.companyDetail,
                data: action.payload,
                isFetching: false,
            };
        },
        [FETCH_PROJECTS_COUNTERS]: (state) => {
            state.projectsCounters = {
                ...state.projectsCounters,
                errors: [],
                isFetching: true,
            };
        },
        [FETCH_PROJECTS_COUNTERS_FAILURE]: (state, action) => {
            state.projectsCounters = {
                ...state.projectsCounters,
                errors: action.payload,
                isFetching: false,
            };
        },
        [FETCH_PROJECTS_COUNTERS_SUCCESS]: (state, action) => {
            state.projectsCounters = {
                ...state.projectsCounters,
                data: action.payload,
                isFetching: false,
            };
        },
        [FETCH_PROPOSALS_TOTALS]: (state) => {
            state.proposalsTotals = {
                ...state.proposalsTotals,
                errors: [],
                isFetching: true,
            };
        },
        [FETCH_PROPOSALS_TOTALS_FAILURE]: (state, action) => {
            state.proposalsTotals = {
                ...state.proposalsTotals,
                errors: action.payload,
                isFetching: false,
            };
        },
        [FETCH_PROPOSALS_TOTALS_SUCCESS]: (state, action) => {
            state.proposalsTotals = {
                ...state.proposalsTotals,
                data: action.payload,
                isFetching: false,
            };
        },
        [INITIALIZE_FILTER_FORM]: (state, action) => {
            state.initialFilterValues = {
                ...state.initialFilterValues,
                ...action.payload,
            };
        },
        [INITIALIZE_FORM]: (state, action) => {
            state.initialValues = { ...state.initialValues, ...action.payload };
        },
        [RESET]: () => INITIAL_STATE,
        [SAVE]: (state) => {
            state.save = { ...state.save, errors: [], isSaving: true };
        },
        [SAVE_FAILURE]: (state, action) => {
            state.save = {
                ...state.save,
                errors: action.payload,
                isSaving: false,
            };
        },
        [SAVE_SUCCESS]: (state, action) => {
            state.save = {
                ...state.save,
                data: action.payload,
                isSaving: false,
            };
        },
    },
});

export const companyGeneralActions = companyGeneralSlice.actions;

export default companyGeneralSlice.reducer;
